import { createSelector } from 'reselect';

import { isScreenBelow640, isScreenBelow1004 } from 'store/common/settings/breakpoint/selectors';
import { PAGES_WITHOUT_TRADE_IN } from 'store/SharedZone/constants';
import { getDomainFromUrl } from 'utils/common';

import { getBodyData, getHeaderData } from '../selectors';

export const getSharedZonePageData = getBodyData;

const knownPunycodeDomains = {
  'фрейм-билайн.дампродам.рф': 'xn----8sbcordeofqx1c.xn--80aakb3bclhj.xn--p1ai',
};

export const getSharedZoneContentSettings = createSelector(getSharedZonePageData, (zone) => {
  const url = zone.contentSettings?.url;
  const domain = getDomainFromUrl(url);

  return {
    ...zone.contentSettings,
    url: url?.replace(domain, knownPunycodeDomains[domain]),
  };
});

export const getSharedZoneCalculateSettingsSettings = (state) =>
  getSharedZoneContentSettings(state).calculateSettings || {};

export const getSharedZonePageSlug = (state) => getSharedZonePageData(state).pageSlug || '';

export const getSharedZonePageHasNavigation = (state) => !!getSharedZonePageData(state).navigation;

export const getSharedZonePageHasTradeInBanner = (state) =>
  !PAGES_WITHOUT_TRADE_IN.includes(getSharedZonePageSlug(state));
export const getSharedZonePageShowBreadcrumbs = (state) =>
  !!getSharedZonePageData(state).showBreadcrumbs;

const getBannerContainerData = (state) => getSharedZonePageData(state).mainBanner || {};
export const getBannerContainerList = (state) => getBannerContainerData(state).banners || [];

// if main banner has ThreeFours

export const getBannerContainerThreeFours = (state) =>
  getSharedZonePageData(state).threeFours || {};
export const getBannerContainerThreeFoursPosition = (state) =>
  getBannerContainerThreeFours(state).position || 0;
export const getBannerContainerThreeFoursEnabled = (state) =>
  getBannerContainerThreeFours(state).enabled || false;

// region info
export const getSharedZoneCurrentRegion = (state) => getHeaderData(state).currentRegion || {};

export const getSharedZoneUserRegionData = (state) =>
  getSharedZoneCurrentRegion(state).userRegion || {};
export const getUserRegionTitle = (state) => getSharedZoneUserRegionData(state).title || '';

export const getSharedZoneFederalRegionData = (state) =>
  getSharedZoneCurrentRegion(state).federalRegion || {};
export const getSharedZoneFederalRegionTitle = (state) =>
  getSharedZoneFederalRegionData(state).title || '';

/**
 * Selectors for banners on the main page
 */
const composeBannerSelector =
  (nestedSelector) =>
  (state, { id }) =>
    nestedSelector(getBannerContainerList(state).find((item) => item.id === id));

const getBannerTitle = (banner) => banner.title || '';
const getBannerUrl = (banner) => banner.url || '';
const getBannerbgColor = (banner) => banner.bgColor || '';
const getBannerTextColor = (banner) => banner.textColor || '';
const getBannerDescription = (banner) => banner.description || '';
const getBannerShortDescription = (banner) => banner.shortDescription || '';
const getBannerbuttonText = (banner) => banner.buttonText || '';
const getBannerBgImageUrl = (banner) => banner.bgImageUrl || '';
const getBannerBgImageMobileUrl = (banner) => banner.bgImageMobileUrl || '';
const getBannerWebpBgImageUrl = (banner) => banner.webpBgImageUrl || '';
const getBannerbWebpMobileBgImageUrl = (banner) => banner.webpMobileBgImageUrl || '';
//  const getBannerArrColor = banner => banner.arrColor || '';

/**
 * Selectors for popular articles widget
 */
export const getSharedZonePopularAricles = (state) => getSharedZonePageData(state).popular || {};
export const getSharedZonePopularAriclesList = (state) =>
  getSharedZonePopularAricles(state).list || [];
export const getSharedZonePopularAriclestags = (state) =>
  getSharedZonePageData(state).popularTags || {};

/**
 * const for delegative class set tablet = 768 < width < 1004, mobile = width < 768
 */
export const getSizeModel = createSelector(
  isScreenBelow1004,
  isScreenBelow640,
  (below1004, below640) => ({ mobile: below640, tablet: below1004 && !below640 }),
);
/**
 * const for delegative framesPerSlide set 1 = width < 768, 2 = 768 < width < 1004, 3 = width >= < 1004
 */
export const getPopularAriclesFramePerSlideRate = createSelector(
  getSizeModel,
  ({ tablet, mobile }) => {
    const defaultFrames = 3;
    if (tablet) return 3;
    if (mobile) return 1;
    return defaultFrames;
  },
);

/**
 * ready for use selectors
 */
export const getmainBannerTitle = composeBannerSelector(getBannerTitle);
export const getmainBannerUrl = composeBannerSelector(getBannerUrl);
export const getmainBannerbgColor = composeBannerSelector(getBannerbgColor);
export const getmainBannerTextColor = composeBannerSelector(getBannerTextColor);
export const getmainBannerDescription = composeBannerSelector(getBannerDescription);
export const getmainBannerShortDescription = composeBannerSelector(getBannerShortDescription);
export const getmainBannerbuttonText = composeBannerSelector(getBannerbuttonText);
export const getmainBannerBgImageUrl = composeBannerSelector(getBannerBgImageUrl);
export const getmainBannerBgImageMobileUrl = composeBannerSelector(getBannerBgImageMobileUrl);
export const getmainBannerWebpBgImageUrl = composeBannerSelector(getBannerWebpBgImageUrl);
export const getmainBannerbWebpMobileBgImageUrl = composeBannerSelector(
  getBannerbWebpMobileBgImageUrl,
);

export const bannersMainSelector = (state) => {
  const blocksList = getBodyData(state)?.blocks?.list ?? [];

  const banners = blocksList.find((block) => block?.id === 'main_page_special');
  if (!banners) {
    return [];
  }

  return banners?.content?.banners ?? [];
};

export const bannersOtherSelector = (state) => {
  const blocksList = getBodyData(state)?.blocks?.list ?? [];

  const banners = blocksList.find((block) => block?.id === 'main_page_other_banners');
  if (!banners) {
    return [];
  }

  const otherBanners = banners?.content?.banners ?? [];
  // хак для корректного отображения
  return otherBanners.slice(0, 2);
};
